import type { GridApi } from 'ag-grid-community';
import type {
  GridState,
  SortModelItem,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { pick } from 'lodash';

export type ReconcileGridSelectionOptions<TData> = {
  api: GridApi<TData>;
  selectedRowId?: string;
  filteredRowIds: Set<string>;
  onMissingSelection?: () => void;
};

// store ag grid state (pagination, sort) in localStorage
export const gridSetStoreState = (
  event: StateUpdatedEvent,
  gridName: string
) => {
  if (!event.api.isDestroyed()) {
    // save the pagination state
    const gridState = pick(event.state, ['pagination', 'sort']);
    localStorage.setItem(`${gridName}State`, JSON.stringify(gridState));
  }
};

// retrieve ag grid state (pagination, sort) from localStorage
export const gridGetStoredState = (
  gridName: string,
  defaultSortModel: SortModelItem[]
): GridState => {
  let state = {
    sort: { sortModel: defaultSortModel },
    pagination: { page: 0, pageSize: 20 }
  };

  try {
    state = JSON.parse(
      localStorage.getItem(`${gridName}State`) ||
        JSON.stringify({
          sort: { sortModel: defaultSortModel },
          pagination: { page: 0, pageSize: 20 }
        })
    );
  } catch (e) {
    console.warn(
      `Failed to parse sort model for grid ${gridName} from localStorage`,
      e
    );
  }

  return { ...state, partialColumnState: true };
};

export const gridSetPage = (
  gridApi: GridApi | null,
  localStorageKey: string
) => {
  const savedState = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
  if (savedState.pagination) {
    gridApi?.paginationGoToPage(savedState.pagination.page);
  }
};

export const syncSelectedGridNode = <TData>(
  api: GridApi<TData>,
  rowId: string
) => {
  const node = api.getRowNode(rowId);

  if (!node) {
    return false;
  }

  node.setSelected(true, true);

  if (node.rowIndex != null) {
    const pageSize = api.paginationGetPageSize();
    api.paginationGoToPage(Math.floor(node.rowIndex / pageSize));
  }

  api.ensureNodeVisible(node, 'middle');
  return true;
};

export const reconcileGridSelection = <TData>({
  api,
  selectedRowId,
  filteredRowIds,
  onMissingSelection
}: ReconcileGridSelectionOptions<TData>) => {
  if (!selectedRowId) {
    api.deselectAll();
    api.redrawRows();
    return;
  }

  if (syncSelectedGridNode(api, selectedRowId)) {
    api.redrawRows();
    return;
  }

  if (!filteredRowIds.has(selectedRowId)) {
    api.deselectAll();
    api.redrawRows();
    onMissingSelection?.();
  }
};
